import { useState, useRef, useEffect, useMemo } from "react";
import { Link, useLocation } from "wouter";
import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient, apiRequest, parseErrorMessage, parseFieldErrors } from "@/lib/queryClient";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ApproveAndPayModal } from "@/components/approve-and-pay-modal";
import { LeaveReviewModal } from "@/components/leave-review-modal";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/hooks/use-toast";
import { type MaintenanceTask, type MaintenanceComment, type MaintenanceTaskMessage, type Property, type TeamMember, type Vendor, type Appliance, type MaintenanceSettings, type Owner, type OwnerVendorApproval, type PropertyGroup, type ServiceFormTemplate, type ServiceFormSubmission, MAINTENANCE_CATEGORIES } from "@shared/schema";
import { isHiddenSectionEnabled } from "@shared/hidden-sections";
import { sortProperties } from "@/lib/property-utils";
import { PadsplitTaskEmblem } from "@/components/padsplit-task-emblem";
import { priorityWithSla } from "@/lib/sla";
import { format } from "date-fns";
import { Send, Home, User, Users, Calendar, Flag, X, Trash2, RefreshCw, AlertCircle, CheckCircle2, Paperclip, Loader2, MessageCircle, ExternalLink, Unlink, CheckCheck, Download, Truck, Wand2, Clock, DollarSign, Star, Bell, Copy, ClipboardCheck, MapPin } from "lucide-react";
import { MessageAttachments } from "@/components/message-attachments";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
// Task #3973 — the comment composer / attachment / @-mention primitives now live
// in the shared internal-comment-thread module so the to-do thread reuses the
// identical UX. Maintenance keeps its own query/mutation and just wires these in.
import {
  AttachmentComposer,
  buildReadyAttachmentUrls,
  CommentItem,
  MentionTextarea,
  extractMentions,
  makeAttachId,
  type PendingTaskAttachment,
} from "@/components/internal-comment-thread";

// A title is "generic" when it's blank or a placeholder, meaning the PM hasn't
// written a meaningful one yet — in that case we offer the AI suggestion.
const GENERIC_TASK_TITLES = new Set([
  "new task", "untitled", "untitled task", "task", "new maintenance task",
  "maintenance task", "new", "n/a", "tbd", "todo", "to do", "no title",
]);

export function isGenericTaskTitle(title: string | null | undefined): boolean {
  const t = (title || "").trim().toLowerCase();
  if (!t) return true;
  return GENERIC_TASK_TITLES.has(t);
}

// Hydrate the AttachmentComposer state from a task's stored URLs so editing
// preserves existing pictures/videos (vs silently dropping them on save).
function hydrateAttachmentsFromUrls(urls: string[] | null | undefined): PendingTaskAttachment[] {
  if (!urls || urls.length === 0) return [];
  return urls.map((url) => {
    let name = "attachment";
    const qIdx = url.indexOf("?");
    if (qIdx >= 0) {
      try {
        const params = new URLSearchParams(url.substring(qIdx + 1));
        const fn = params.get("fn");
        if (fn) name = fn;
      } catch { /* fallthrough */ }
    }
    return { id: makeAttachId(), name, status: "ready" as const, objectPath: url };
  });
}

const PRIORITY_COLORS: Record<string, string> = {
  urgent: "bg-red-100 text-red-800",
  high: "bg-orange-100 text-orange-800",
  medium: "bg-yellow-100 text-yellow-800",
  low: "bg-green-100 text-green-800",
  batch: "bg-slate-100 text-slate-700",
};

const STATUS_LABELS: Record<string, string> = {
  requests: "Requests",
  open: "Open",
  in_progress: "In Progress",
  on_hold: "On Hold",
  complete: "Complete",
};

function PadsplitSyncBadge({
  task,
  property,
  taskId,
}: {
  task: MaintenanceTask & { syncToPadsplit?: boolean; padsplitTaskId?: string | null; padsplitPushError?: string | null };
  property: Property | null | undefined;
  taskId: number;
}) {
  const { toast } = useToast();
  const propertyHasPadsplit = !!property?.padsplitPropertyId;
  const showComingSoon = () => toast({ title: "Coming soon" });

  // Determine current state. Order matters: a non-PadSplit property wins
  // because the toggle is moot in that case — EXCEPT when the task is already
  // synced (padsplitTaskId present): then we still show the PadSplit ID, with
  // the emblem degrading to a non-clickable span since no link is resolvable.
  if (!propertyHasPadsplit && !task.padsplitTaskId) {
    return (
      <div className="text-xs text-gray-500 flex items-center gap-1.5" data-testid="badge-padsplit-no-property">
        <AlertCircle className="h-3.5 w-3.5" />
        Not on PadSplit (no PadSplit property ID)
      </div>
    );
  }
  if (task.syncToPadsplit === false) {
    return (
      <div className="text-xs text-gray-500 flex items-center gap-1.5" data-testid="badge-padsplit-disabled">
        <AlertCircle className="h-3.5 w-3.5" />
        Sync to PadSplit disabled for this task
      </div>
    );
  }
  if (task.padsplitPushError) {
    return (
      <div className="flex flex-col gap-1.5" data-testid="badge-padsplit-failed">
        <div className="flex items-center gap-2 rounded-md border border-red-200 bg-red-50 px-2 py-1.5">
          <AlertCircle className="h-3.5 w-3.5 text-red-600" />
          <span className="text-xs text-red-700 flex-1">PadSplit sync failed — {task.padsplitPushError}</span>
        </div>
        {task.padsplitTaskId && (
          <PadsplitTaskEmblem
            padsplitTaskId={task.padsplitTaskId}
            padsplitPropertyId={property?.padsplitPropertyId}
            testId={`badge-padsplit-emblem-detail-${taskId}`}
          />
        )}
      </div>
    );
  }
  if (task.padsplitTaskId) {
    return (
      <div className="flex flex-col gap-1.5" data-testid="badge-padsplit-synced">
        <div className="text-xs text-green-700 flex items-center gap-1.5">
          <CheckCircle2 className="h-3.5 w-3.5" />
          Synced to PadSplit
        </div>
        <PadsplitTaskEmblem
          padsplitTaskId={task.padsplitTaskId}
          padsplitPropertyId={property?.padsplitPropertyId}
          testId={`badge-padsplit-emblem-detail-${taskId}`}
        />
      </div>
    );
  }
  // Eligible but never pushed (e.g. created before this feature shipped).
  return (
    <div className="flex items-center gap-2 rounded-md border border-gray-200 bg-gray-50 px-2 py-1.5" data-testid="badge-padsplit-pending">
      <AlertCircle className="h-3.5 w-3.5 text-gray-500" />
      <span className="text-xs text-gray-600 flex-1">Not yet pushed to PadSplit</span>
    </div>
  );
}

// Task #1654 — Host-side chat thread with the assigned vendor. The vendor sees
// the mirror of this panel inside the vendor portal's maintenance detail view.
function VendorChatPanel({ taskId, vendorName }: { taskId: number; vendorName: string }) {
  const { toast } = useToast();
  const [draft, setDraft] = useState("");
  const [chatAttachments, setChatAttachments] = useState<PendingTaskAttachment[]>([]);

  const { data: messages = [], isLoading } = useQuery<MaintenanceTaskMessage[]>({
    queryKey: ["/api/app/tasks", taskId, "vendor-messages"],
    queryFn: async () => {
      const r = await fetch(`/api/app/tasks/${taskId}/vendor-messages`, { credentials: "include" });
      if (!r.ok) throw new Error("Failed to load vendor chat");
      return r.json();
    },
    refetchInterval: 15000,
  });

  const sendMutation = useMutation({
    mutationFn: ({ body, attachments }: { body: string; attachments: string[] }) =>
      apiRequest("POST", `/api/app/tasks/${taskId}/vendor-messages`, { body, attachments }),
    onSuccess: () => {
      setDraft("");
      setChatAttachments([]);
      queryClient.invalidateQueries({ queryKey: ["/api/app/tasks", taskId, "vendor-messages"] });
    },
    onError: (e: Error) => toast({ title: "Error", description: parseErrorMessage(e, "Failed to send message."), variant: "destructive" }),
  });

  // Task #1716 — Mark vendor→host messages read whenever the chat panel is
  // open and has surfaced unread vendor messages. Triggers on mount and on
  // every poll that surfaces new vendor messages so the host task-list
  // `unreadVendorMessageCount` badge clears immediately rather than waiting
  // for the user to navigate away and back.
  const markReadMutation = useMutation({
    meta: { silentSave: true },
    mutationFn: () => apiRequest("PATCH", `/api/app/tasks/${taskId}/vendor-messages/read`, {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/tasks"] });
    },
  });
  const unreadVendorMessageIdsKey = messages
    .filter((m) => m.senderType !== "host" && !m.readAtHost)
    .map((m) => m.id)
    .join(",");
  useEffect(() => {
    if (unreadVendorMessageIdsKey.length === 0) return;
    if (markReadMutation.isPending) return;
    markReadMutation.mutate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unreadVendorMessageIdsKey]);

  function handleSend() {
    if (sendMutation.isPending) return;
    if (chatAttachments.some((a) => a.status === "uploading")) {
      toast({ title: "Wait for uploads to finish" });
      return;
    }
    const trimmed = draft.trim();
    const attachments = buildReadyAttachmentUrls(chatAttachments);
    if (!trimmed && attachments.length === 0) return;
    sendMutation.mutate({ body: trimmed, attachments });
  }

  return (
    <div className="border-t border-gray-200 pt-4">
      <h3 className="font-medium text-sm text-gray-900 mb-3 flex items-center gap-2">
        <MessageCircle className="h-4 w-4" />Vendor chat with {vendorName}
      </h3>
      <div className="mb-3 max-h-64 overflow-y-auto space-y-2" data-testid="list-vendor-chat">
        {isLoading ? (
          <p className="text-xs text-gray-400 text-center py-4">Loading…</p>
        ) : messages.length === 0 ? (
          <p className="text-xs text-gray-400 text-center py-4">No messages yet.</p>
        ) : (
          messages.map(m => {
            if (m.senderType === "system") {
              return (
                <div key={m.id} className="flex justify-center py-1" data-testid={`row-vendor-chat-${m.id}`}>
                  <p className="text-xs text-gray-400 italic px-3 py-1 bg-gray-50 rounded-full border border-gray-100" data-testid={`badge-system-message-${m.id}`}>
                    {m.body}
                  </p>
                </div>
              );
            }
            const fromHost = m.senderType === "host";
            const msgAttachments = Array.isArray(m.attachments) ? m.attachments as string[] : [];
            return (
              <div key={m.id} className={`flex ${fromHost ? "justify-end" : "justify-start"}`} data-testid={`row-vendor-chat-${m.id}`}>
                <div className={`max-w-[80%] rounded-lg px-3 py-2 text-sm ${fromHost ? "bg-blue-600 text-white" : "bg-gray-100 text-gray-900"}`}>
                  {m.body ? <p className="whitespace-pre-wrap break-words">{m.body}</p> : null}
                  {msgAttachments.length > 0 && (
                    <MessageAttachments urls={msgAttachments} messageId={`vchat-${m.id}`} tone={fromHost ? "dark" : "light"} />
                  )}
                  <p className={`text-[10px] mt-1 flex items-center gap-1 ${fromHost ? "text-blue-100 justify-end" : "text-gray-500"}`}>
                    <span>{format(new Date(m.createdAt), "MMM d, h:mm a")}</span>
                    {fromHost && m.readAtVendor ? (
                      <span className="flex items-center gap-0.5" data-testid={`status-seen-vendor-chat-${m.id}`} title={`Seen ${format(new Date(m.readAtVendor), "MMM d, h:mm a")}`}>
                        <CheckCheck className="h-3 w-3" />
                        <span>Seen</span>
                      </span>
                    ) : null}
                  </p>
                </div>
              </div>
            );
          })
        )}
      </div>
      <div className="space-y-2">
        <AttachmentComposer
          attachments={chatAttachments}
          setAttachments={setChatAttachments}
          disabled={sendMutation.isPending}
          testIdPrefix="vendor-chat-attach"
        />
        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleSend();
          }}
          className="flex gap-2"
        >
          <Textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder={`Message ${vendorName}…`}
            className="flex-1 min-h-[44px] text-sm"
            data-testid="input-vendor-chat"
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleSend();
              }
            }}
          />
          <Button
            type="submit"
            size="sm"
            disabled={(!draft.trim() && chatAttachments.filter(a => a.status === "ready").length === 0) || sendMutation.isPending}
            data-testid="button-send-vendor-chat"
          >
            <Send className="h-4 w-4" />
          </Button>
        </form>
      </div>
    </div>
  );
}

function applianceLabel(a: Pick<Appliance, "id" | "make" | "model" | "serialNumber">): string {
  const head = [a.make, a.model].filter(Boolean).join(" ") || `Appliance #${a.id}`;
  return a.serialNumber ? `${head} — ${a.serialNumber}` : head;
}

function LinkedApplianceEditField({
  editData,
  setEditData,
  editFieldErrors,
  clearFieldError,
}: {
  editData: Partial<MaintenanceTask>;
  setEditData: (d: Partial<MaintenanceTask>) => void;
  editFieldErrors: Record<string, string>;
  clearFieldError: (field: string) => void;
}) {
  const propertyId = editData.propertyId ?? null;
  const query = useQuery<Appliance[]>({
    queryKey: ["/api/app/properties", propertyId, "appliances"],
    queryFn: async () => {
      if (!propertyId) return [];
      const res = await fetch(`/api/app/properties/${propertyId}/appliances`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load appliances");
      return res.json();
    },
    enabled: !!propertyId,
  });
  const appliances = query.data || [];
  return (
    <div>
      <label className="text-xs font-medium text-gray-600 mb-1 block">Linked Appliance</label>
      <Select
        value={editData.linkedApplianceId != null ? String(editData.linkedApplianceId) : "none"}
        onValueChange={(v) => {
          setEditData({ ...editData, linkedApplianceId: v === "none" ? null : parseInt(v, 10) });
          clearFieldError("linkedApplianceId");
        }}
        disabled={!propertyId}
      >
        <SelectTrigger
          className={`h-8 text-xs${editFieldErrors.linkedApplianceId ? " border-red-500 focus:ring-red-500" : ""}`}
          data-testid="select-edit-linked-appliance"
        >
          <SelectValue placeholder={propertyId ? "Select appliance" : "Select a property first"} />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="none">No appliance</SelectItem>
          {appliances.length === 0 && propertyId ? (
            <div className="px-2 py-1.5 text-xs text-muted-foreground" data-testid="text-no-appliances-edit">
              No appliances on this property yet.
            </div>
          ) : (
            appliances.map((a) => (
              <SelectItem key={a.id} value={String(a.id)} data-testid={`option-edit-linked-appliance-${a.id}`}>
                {applianceLabel(a)}
              </SelectItem>
            ))
          )}
        </SelectContent>
      </Select>
      {editFieldErrors.linkedApplianceId && (
        <p className="text-xs text-red-600 mt-1" data-testid="error-edit-linked-appliance">{editFieldErrors.linkedApplianceId}</p>
      )}
    </div>
  );
}

export default function MaintenanceTaskDetail({ taskId, properties, teamMembers, focusCommentId, onClose }: {
  taskId: number;
  properties: Property[];
  teamMembers: TeamMember[];
  focusCommentId?: number | null;
  onClose: () => void;
}) {
  const { toast } = useToast();
  const [, navigate] = useLocation();
  const [commentText, setCommentText] = useState("");
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState<Partial<MaintenanceTask>>({});
  const [editFieldErrors, setEditFieldErrors] = useState<Record<string, string>>({});
  const [commentError, setCommentError] = useState<string | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [provenPayOpen, setProvenPayOpen] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);
  // Task #1303 follow-up — staged uploads for the comment composer and the
  // task edit form. Both flows reuse `AttachmentComposer` above which writes
  // directly to object storage via the maintenance upload-url endpoint.
  const [commentAttachments, setCommentAttachments] = useState<PendingTaskAttachment[]>([]);
  const [editAttachments, setEditAttachments] = useState<PendingTaskAttachment[]>([]);

  const FIELD_LABELS: Record<string, string> = {
    title: "Title",
    description: "Description",
    category: "Category",
    status: "Status",
    priority: "Priority",
    propertyId: "Property",
    assigneeId: "Assignee",
    vendorId: "Vendor",
    reporterMemberId: "Reported by",
    linkedApplianceId: "Linked Appliance",
    dueDate: "Due Date",
  };
  const EDIT_FIELD_NAMES = new Set(Object.keys(FIELD_LABELS));

  function clearFieldError(field: string) {
    setEditFieldErrors((prev) => {
      if (!(field in prev)) return prev;
      const { [field]: _, ...rest } = prev;
      return rest;
    });
  }

  const userQuery = useQuery<{ user: { id: number; name?: string | null; email: string } }>({ queryKey: ["/api/app/auth/me"] });
  const currentUser = userQuery.data?.user;

  const taskQuery = useQuery<MaintenanceTask>({
    queryKey: ["/api/app/tasks", taskId],
    queryFn: async () => {
      const res = await fetch(`/api/app/tasks/${taskId}`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load task");
      return res.json();
    },
    refetchInterval: 30000,
    refetchOnWindowFocus: true,
  });

  // Forms-on-tasks — resolved template + submissions for a form-task.
  const taskFormQuery = useQuery<{ template: ServiceFormTemplate; submissions: ServiceFormSubmission[] }>({
    queryKey: ["/api/app/tasks", taskId, "form"],
    enabled: !!taskQuery.data?.formTemplateId,
  });

  const settingsQuery = useQuery<MaintenanceSettings>({ queryKey: ["/api/app/tasks/settings"] });
  const slaConfig = settingsQuery.data?.slaConfig ?? null;

  const commentsQuery = useQuery<MaintenanceComment[]>({
    queryKey: ["/api/app/tasks", taskId, "comments"],
    queryFn: async () => {
      const res = await fetch(`/api/app/tasks/${taskId}/comments`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load comments");
      return res.json();
    },
  });

  type LinkedChat = {
    chatId: number;
    memberId: number;
    senderName: string;
    senderType: string;
    messageText: string;
    createdAt: string;
    linkType: string | null;
    attachmentUrls: string[];
  };
  // Task #2024 — host-facing checklist with audit trail. The GET response
  // includes `completedByName` so toggled items render "Checked <date> by
  // <name>" inline, matching the task-export PDF wording from Task #2015.
  type ChecklistItem = {
    id: number;
    taskId: number;
    orderIndex: number;
    text: string;
    photoRequired: boolean;
    completedAt: string | null;
    completedById: number | null;
    completedByName: string | null;
    photoUrl: string | null;
    createdAt: string;
  };
  const checklistQuery = useQuery<ChecklistItem[]>({
    queryKey: ["/api/app/tasks", taskId, "checklist"],
    queryFn: async () => {
      const res = await fetch(`/api/app/tasks/${taskId}/checklist`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load checklist");
      return res.json();
    },
  });
  const toggleChecklistMutation = useMutation({
    mutationFn: async ({ itemId, checked }: { itemId: number; checked: boolean }) => {
      const res = await apiRequest(
        "PATCH",
        `/api/app/tasks/${taskId}/checklist/${itemId}`,
        { completedAt: checked ? new Date().toISOString() : null },
      );
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/tasks", taskId, "checklist"] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/tasks", taskId] });
    },
    onError: (e: Error) =>
      toast({ title: "Couldn't update checklist", description: parseErrorMessage(e, "Try again in a moment."), variant: "destructive" }),
  });

  const linkedChatsQuery = useQuery<LinkedChat[]>({
    queryKey: ["/api/app/tasks", taskId, "linked-chats"],
    queryFn: async () => {
      const res = await fetch(`/api/app/tasks/${taskId}/linked-chats`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load linked messages");
      return res.json();
    },
  });

  type StatusHistoryEntry = {
    fromStatus: string | null;
    toStatus: string;
    changedByName: string | null;
    changedAt: string;
    note: string | null;
    actorType: "host" | "vendor" | "system";
  };
  const statusHistoryQuery = useQuery<StatusHistoryEntry[]>({
    queryKey: ["/api/app/tasks", taskId, "status-history"],
    queryFn: async () => {
      const res = await fetch(`/api/app/operations/tasks/${taskId}/status-history`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load status history");
      return res.json();
    },
  });

  // Task #1533 — let hosts unlink a chat directly from the ticket detail's
  // "Linked messages" section. Mirrors the unlink mutation in
  // app-messages.tsx so the green "Ticket #N" pill on the bubble clears and
  // the row disappears from the linked-chats list. Existing comments and
  // attachments posted when the link was created are intentionally left in
  // place to preserve the audit trail.
  //
  // Task #2090 — track which specific chat IDs are pending so each button
  // is independently disabled. Using a shared `unlinkChatMutation.isPending`
  // was disabling ALL "Remove link" buttons simultaneously, which prevented
  // subsequent unlinks in a multi-linked-message task.
  const [unlinkingChatIds, setUnlinkingChatIds] = useState<Set<number>>(new Set());

  const unlinkChatMutation = useMutation({
    mutationFn: async (chatId: number) => {
      const res = await apiRequest("DELETE", `/api/app/member-chats/${chatId}/link-to-maintenance`);
      return res.json() as Promise<{ unlinkedTaskId: number | null }>;
    },
    onSuccess: (_data, chatId) => {
      // Capture memberId from the query data before invalidation drops the row.
      const memberId = (linkedChatsQuery.data ?? []).find((lc) => lc.chatId === chatId)?.memberId;
      queryClient.invalidateQueries({ queryKey: ["/api/app/tasks", taskId, "linked-chats"] });
      // Task #1932 — the server rewrites the original "Message from …"
      // comment in place on unlink, so refresh the comments thread too or
      // the sheet would keep showing the stale quote until reload.
      queryClient.invalidateQueries({ queryKey: ["/api/app/tasks", taskId, "comments"] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/tasks", taskId] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/tasks"] });
      if (memberId != null) {
        queryClient.invalidateQueries({ queryKey: ["/api/app/members", memberId, "timeline"] });
      }
      toast({ title: "Chat unlinked" });
    },
    onError: (e: Error) =>
      toast({ title: "Couldn't unlink chat", description: parseErrorMessage(e, "Try again in a moment."), variant: "destructive" }),
    onSettled: (_data, _error, chatId) => {
      // Remove from the per-chat pending set regardless of success or error.
      setUnlinkingChatIds((prev) => {
        const next = new Set(prev);
        next.delete(chatId);
        return next;
      });
    },
  });

  const [chatToUnlink, setChatToUnlink] = useState<number | null>(null);

  function confirmUnlinkChat(chatId: number) {
    setChatToUnlink(chatId);
  }

  const [suggestedTitle, setSuggestedTitle] = useState<string | null>(null);
  // Tracks whether the host has typed in the title input during this edit
  // session. Once true, a held AI suggestion can never resurface a destructive
  // pill over the host's text — even on AI Draft re-runs — so protection no
  // longer relies solely on the live title value being "generic". (Task #2657)
  const [titleManuallyEdited, setTitleManuallyEdited] = useState(false);

  const draftDescriptionMutation = useMutation({
    meta: { silentSave: true },
    mutationFn: async () => {
      const res = await apiRequest("POST", `/api/app/operations/tasks/${taskId}/draft-description`);
      return res.json() as Promise<{ description: string; suggestedTitle?: string }>;
    },
    onSuccess: (data) => {
      setEditData((prev) => ({ ...prev, description: data.description }));
      clearFieldError("description");
      setSuggestedTitle((data.suggestedTitle || "").trim() || null);
      toast({ title: "AI description drafted", description: "Review and edit before saving." });
    },
    onError: (e: Error) =>
      toast({ title: "Couldn't draft description", description: parseErrorMessage(e, "Try again in a moment."), variant: "destructive" }),
  });

  const vendorsQuery = useQuery<Vendor[]>({
    queryKey: ["/api/app/operations/vendors"],
  });

  // Task #3841 — Mirror the Vendor Payments owner pre-approval affordance here.
  // To decide whether this task needs explicit owner sign-off we need (a) the
  // owners with their pre-approved thresholds and the property groups they own,
  // and (b) any approval already raised for this task. The owners endpoint is
  // PMPro + owner-portal beta gated; for hosts without access it simply 403s and
  // `data` stays undefined, so the affordance never appears.
  type OwnersResponse = {
    owners: Owner[];
    groupsByOwner: Record<number, PropertyGroup[]>;
  };
  const ownersQuery = useQuery<OwnersResponse>({
    queryKey: ["/api/app/operations/owners"],
    retry: false,
  });
  const ownerApprovalsQuery = useQuery<OwnerVendorApproval[]>({
    queryKey: ["/api/app/owner-vendor-approvals"],
    retry: false,
  });

  const task = taskQuery.data;
  const comments = commentsQuery.data || [];
  const vendors = vendorsQuery.data || [];

  // Resolve a task's owner from its property's group name. Properties link to a
  // group by the TEXT match `properties.propertyGroup === propertyGroups.name`;
  // a group links to an owner via `propertyGroups.ownerId`.
  const ownersById = useMemo(
    () => new Map((ownersQuery.data?.owners || []).map((o) => [o.id, o])),
    [ownersQuery.data],
  );
  const ownerByGroupName = useMemo(() => {
    const map = new Map<string, Owner>();
    const groupsByOwner = ownersQuery.data?.groupsByOwner || {};
    for (const [ownerIdStr, groups] of Object.entries(groupsByOwner)) {
      const owner = ownersById.get(Number(ownerIdStr));
      if (!owner) continue;
      for (const g of groups) {
        if (g?.name) map.set(g.name, owner);
      }
    }
    return map;
  }, [ownersQuery.data, ownersById]);
  const ownerApprovalForTask = useMemo(
    () => (ownerApprovalsQuery.data || []).find((a) => a.taskId === taskId) ?? null,
    [ownerApprovalsQuery.data, taskId],
  );
  // Task #4045 — While an approval row for this task is "pending", the owner
  // must sign off before the task can be scheduled or started. The backend
  // enforces this (409 owner_approval_pending); the UI disables the affected
  // actions and shows a hard banner.
  const ownerApprovalPending = ownerApprovalForTask?.status === "pending";

  const activePropertyId = editing && editData.propertyId !== undefined ? editData.propertyId : task?.propertyId;

  // Task #1425 — Reporter member picker (mirrors the create dialog).
  // Re-fetches when the property changes so the dropdown stays scoped.
  const reporterMembersQuery = useQuery<Array<{ id: number; name: string; displayName?: string | null; email?: string | null }>>({
    queryKey: ["/api/app/tasks/property-members", activePropertyId],
    queryFn: async () => {
      if (!activePropertyId) return [];
      const res = await fetch(`/api/app/tasks/property-members?propertyId=${activePropertyId}`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load members");
      return res.json();
    },
    enabled: !!activePropertyId,
  });
  const reporterMembers = reporterMembersQuery.data || [];
  const taskPropertyId = task?.propertyId;
  const linkedAppliancePropertyId = taskPropertyId ?? activePropertyId;
  const linkedAppliancesQuery = useQuery<Appliance[]>({
    queryKey: ["/api/app/properties", linkedAppliancePropertyId, "appliances"],
    queryFn: async () => {
      if (!linkedAppliancePropertyId) return [];
      const res = await fetch(`/api/app/properties/${linkedAppliancePropertyId}/appliances`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load appliances");
      return res.json();
    },
    enabled: !!linkedAppliancePropertyId,
  });
  const linkedAppliance = task?.linkedApplianceId != null
    ? (linkedAppliancesQuery.data || []).find((a) => a.id === task.linkedApplianceId) || null
    : null;
  const reporterMemberId = task?.reporterMemberId ?? null;
  const reporter = reporterMemberId != null ? reporterMembers.find((m) => m.id === reporterMemberId) : null;
  // Room Turn tasks are cleaning jobs, so only vendors carrying the "cleaning"
  // role (VENDOR_ROLES) are eligible; every other category shows all
  // property-scoped vendors.
  const activeCategory = editing && editData.category !== undefined ? editData.category : task?.category;
  const propertyVendors = (activePropertyId
    ? vendors.filter((v) => !v.propertyIds || (v.propertyIds as number[]).length === 0 || (v.propertyIds as number[]).includes(activePropertyId))
    : vendors)
    .filter((v) =>
      activeCategory === "Room Turn"
        ? (Array.isArray(v.roles) ? (v.roles as string[]) : []).includes("cleaning")
        : true,
    )
    .slice().sort((a, b) => {
      const pa = a.priority ?? Number.MAX_SAFE_INTEGER;
      const pb = b.priority ?? Number.MAX_SAFE_INTEGER;
      if (pa !== pb) return pa - pb;
      return (a.name || "").localeCompare(b.name || "");
    });

  useEffect(() => {
    if (task && !editing) {
      setEditData({
        title: task.title,
        description: task.description,
        status: task.status as MaintenanceTask["status"],
        priority: task.priority as MaintenanceTask["priority"],
        propertyId: task.propertyId,
        assigneeId: task.assigneeId,
        vendorId: task.vendorId,
        reporterMemberId: task.reporterMemberId ?? null,
        dueDate: task.dueDate,
      });
    }
  }, [task]);

  // Task #1612 — Explicit "Send to vendor" action. Stamps sentToVendorAt
  // server-side and fires SMS+email to the vendor. The button is only
  // shown when a vendor is assigned but the task hasn't been sent yet
  // (the badge replaces it once sent).
  const sendToVendorMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", `/api/app/tasks/${taskId}/send-to-vendor`, {});
      return res.json() as Promise<MaintenanceTask & { vendorNotify?: { sms: boolean; email: boolean; reason?: string }; autoPromoted?: boolean }>;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/tasks"] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/tasks", taskId] });
      const n = data.vendorNotify;
      const channels = n ? [n.sms ? "SMS" : null, n.email ? "email" : null].filter(Boolean).join(" + ") : "";
      if (channels) {
        toast({ title: `Sent to vendor (${channels})` });
      } else {
        toast({
          title: "Couldn't reach vendor",
          description: "We marked the task as sent but couldn't deliver SMS or email. Check the vendor's contact info.",
          variant: "destructive",
        });
      }
      if (data.autoPromoted) {
        toast({ title: "Task moved to Open", description: "Status was automatically updated so the vendor can act on it." });
      }
    },
    onError: (e: Error) => {
      toast({ title: "Send failed", description: parseErrorMessage(e, "Try again in a moment."), variant: "destructive" });
    },
  });

  // Re-fire the owner-approval notification (in-app / email / SMS) for a task
  // that's still awaiting the owner's decision, so the host can nudge them
  // straight from this panel without leaving the maintenance board.
  const resendApprovalMutation = useMutation({
    mutationFn: () => apiRequest("POST", `/api/app/owner-portal/task-approvals/${taskId}/resend`, {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/owner-vendor-approvals"] });
      toast({ title: "Reminder sent", description: "The owner was notified again about this approval." });
    },
    onError: (e: Error) =>
      toast({ title: "Couldn't send reminder", description: parseErrorMessage(e, "Try again in a moment."), variant: "destructive" }),
  });

  const updateMutation = useMutation({
    mutationFn: async (data: Partial<MaintenanceTask>) => {
      const payload: Record<string, unknown> = { ...data };

      const toIsoOrNull = (v: unknown): string | null => {
        if (v == null) return null;
        const d = v instanceof Date ? v : new Date(v as string);
        if (isNaN(d.getTime())) return null;
        return d.toISOString();
      };

      const originalIso = toIsoOrNull(task?.dueDate);
      const editedIso = toIsoOrNull(data.dueDate);

      if (editedIso !== originalIso) {
        payload.dueDate = editedIso;
      } else {
        delete payload.dueDate;
      }

      return apiRequest("PATCH", `/api/app/tasks/${taskId}`, payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/tasks"] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/tasks", taskId] });
      setEditing(false);
      setEditFieldErrors({});
      toast({ title: "Task updated" });
    },
    onError: (e: Error) => {
      const fieldErrors = parseFieldErrors(e);
      const fieldKeys = Object.keys(fieldErrors);
      if (fieldKeys.length > 0) {
        const applied: Record<string, string> = {};
        for (const key of fieldKeys) {
          if (EDIT_FIELD_NAMES.has(key)) {
            applied[key] = fieldErrors[key];
          }
        }
        if (Object.keys(applied).length > 0) {
          setEditFieldErrors(applied);
          toast({
            title: "Please fix the highlighted fields",
            description: Object.entries(applied)
              .map(([k, v]) => `${FIELD_LABELS[k] || k}: ${v}`)
              .join(" "),
            variant: "destructive",
          });
          return;
        }
      }
      setEditFieldErrors({});
      toast({
        title: "Failed to update task",
        description: parseErrorMessage(e, "Something went wrong while saving your changes."),
        variant: "destructive",
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async () => {
      return apiRequest("DELETE", `/api/app/tasks/${taskId}`, undefined);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/tasks"] });
      onClose();
      toast({ title: "Task deleted" });
    },
    onError: (e: Error) => toast({
      title: "Failed to delete task",
      description: parseErrorMessage(e, "We couldn't delete this task. Please try again."),
      variant: "destructive",
    }),
  });

  // Forms-on-tasks — nudge the assigned vendor (in-app notification + SMS).
  const remindMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", `/api/app/tasks/${taskId}/remind`, {});
      return res.json() as Promise<{ reminded: boolean; smsSent?: boolean; smsError?: string }>;
    },
    onSuccess: (data) => {
      toast({
        title: "Vendor reminded",
        description: data.smsSent ? "In-app notification and SMS sent." : "In-app notification sent.",
      });
    },
    onError: (e: Error) => toast({
      title: "Failed to remind vendor",
      description: parseErrorMessage(e, "We couldn't send the reminder. Please try again."),
      variant: "destructive",
    }),
  });

  // Forms-on-tasks — copy this task's spec into a fresh open task and jump to it.
  const duplicateMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", `/api/app/tasks/${taskId}/duplicate`, {});
      return res.json() as Promise<{ id: number }>;
    },
    onSuccess: (copy) => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/tasks"] });
      toast({ title: "Task duplicated" });
      navigate(`/app/tasks?task=${copy.id}`);
    },
    onError: (e: Error) => toast({
      title: "Failed to duplicate task",
      description: parseErrorMessage(e, "We couldn't duplicate this task. Please try again."),
      variant: "destructive",
    }),
  });

  const commentMutation = useMutation({
    mutationFn: async ({ content, mentions, attachmentUrls }: { content: string; mentions: number[]; attachmentUrls: string[] }) => {
      return apiRequest("POST", `/api/app/tasks/${taskId}/comments`, { content, mentions, attachmentUrls });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/tasks", taskId, "comments"] });
      setCommentText("");
      setCommentError(null);
      setCommentAttachments([]);
    },
    onError: (e: Error) => {
      const fieldErrors = parseFieldErrors(e);
      if (fieldErrors.content) {
        setCommentError(fieldErrors.content);
        toast({
          title: "Please fix your comment",
          description: fieldErrors.content,
          variant: "destructive",
        });
        return;
      }
      const message = parseErrorMessage(e, "We couldn't post your comment. Please try again.");
      setCommentError(message);
      toast({ title: "Failed to post comment", description: message, variant: "destructive" });
    },
  });

  function handlePostComment() {
    if (!commentText.trim() && commentAttachments.filter((a) => a.status === "ready").length === 0) return;
    if (commentAttachments.some((a) => a.status === "uploading")) {
      toast({ title: "Wait for uploads to finish" });
      return;
    }
    const mentions = extractMentions(commentText, teamMembers);
    commentMutation.mutate({
      content: commentText,
      mentions,
      attachmentUrls: buildReadyAttachmentUrls(commentAttachments),
    });
  }

  const property = task?.propertyId ? properties.find((p) => p.id === task.propertyId) : null;
  const assignee = task?.assigneeId != null ? teamMembers.find((m) => m.id === task.assigneeId) : null;

  // In-flight dismissal gating (docs/dialog-dismissal-convention.md). This Sheet
  // wraps several multi-second writes — save, comment post, Gemini draft, send to
  // vendor — plus presigned attachment uploads. A stray X-button / Escape /
  // backdrop click mid-request would silently drop the user's work, so block
  // every dismissal path while any of those are in flight.
  const isInFlight =
    updateMutation.isPending ||
    commentMutation.isPending ||
    draftDescriptionMutation.isPending ||
    sendToVendorMutation.isPending ||
    commentAttachments.some((a) => a.status === "uploading") ||
    editAttachments.some((a) => a.status === "uploading");

  return (
    <>
    <Sheet open onOpenChange={(open) => { if (!open && isInFlight) return; if (!open) onClose(); }}>
      <SheetContent
        className="w-full sm:w-[480px] sm:max-w-[480px] overflow-y-auto"
        data-testid="sheet-task-detail"
        onEscapeKeyDown={(e) => { if (isInFlight) e.preventDefault(); }}
        onInteractOutside={(e) => { if (isInFlight) e.preventDefault(); }}
        onPointerDownOutside={(e) => { if (isInFlight) e.preventDefault(); }}
      >
        {taskQuery.isLoading ? (
          <div className="flex items-center justify-center h-32 text-gray-400">Loading...</div>
        ) : !task ? (
          <div className="flex items-center justify-center h-32 text-gray-400">Task not found</div>
        ) : (<>
        <SheetHeader>
          <div className="flex items-start justify-between">
            <SheetTitle className="text-left pr-4">{task.title}</SheetTitle>
          </div>
        </SheetHeader>

        <div className="mt-4 space-y-4">
          <div className="flex items-center gap-2 flex-wrap">
            <Badge className={PRIORITY_COLORS[task.priority]}>{task.priority}</Badge>
            <Badge variant="outline">{STATUS_LABELS[task.status] || task.status}</Badge>
            <Badge variant="outline" className="text-xs">{task.source}</Badge>
            {task.isSample && (
              <Badge variant="outline" className="text-xs bg-amber-50 dark:bg-amber-950/40 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-800" data-testid="badge-sample-task">
                Sample
              </Badge>
            )}
            {task.paymentRequired && (
              <Badge variant="outline" className="text-xs bg-blue-50 dark:bg-blue-950/40 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-800" data-testid="badge-payment-required">
                Payment required{task.priceCents ? ` · $${(task.priceCents / 100).toFixed(2)}` : ""}
              </Badge>
            )}
          </div>

          <PadsplitSyncBadge task={task} property={property} taskId={taskId} />

          {/* B19 — the trash service is switched off in shared/hidden-sections.ts. */}
          {task.category === "Trash" && isHiddenSectionEnabled("trash_service") && (
            <Button
              variant="outline"
              size="sm"
              className="w-full"
              disabled={task.propertyId == null}
              onClick={() =>
                navigate(
                  `/app/operations/trash?onDemand=1&propertyId=${task.propertyId}&taskId=${task.id}`,
                )
              }
              data-testid="button-task-on-demand-trash"
            >
              <Truck className="h-4 w-4 mr-1.5" />
              {task.propertyId == null
                ? "Set a property to request pickup"
                : "Request on-demand trash pickup"}
            </Button>
          )}

          {!editing ? (
            <div className="space-y-3">
              {task.description && (
                <p className="text-sm text-gray-600">{task.description}</p>
              )}
              {task.attachmentUrls && task.attachmentUrls.length > 0 && (
                <MessageAttachments urls={task.attachmentUrls} messageId={`task-${task.id}`} />
              )}
              <div className="grid grid-cols-2 gap-3 text-sm">
                {property && (
                  <div className="flex items-center gap-2 text-gray-600">
                    <Home className="h-4 w-4 text-gray-400" />
                    <span>{property.name}</span>
                  </div>
                )}
                {task.category && (
                  <div className="flex items-center gap-2 text-gray-600">
                    <Flag className="h-4 w-4 text-gray-400" />
                    <span>{task.category}</span>
                  </div>
                )}
                {task.linkedApplianceId != null && (
                  <div className="flex items-center gap-2 text-gray-600" data-testid="text-task-linked-appliance">
                    <Flag className="h-4 w-4 text-blue-500" />
                    <span>
                      Appliance: {linkedAppliance
                        ? ([linkedAppliance.make, linkedAppliance.model].filter(Boolean).join(" ") || `Appliance #${linkedAppliance.id}`)
                        : `#${task.linkedApplianceId}`}
                    </span>
                  </div>
                )}
                {assignee && (
                  <div className="flex items-center gap-2 text-gray-600" data-testid="text-task-assignee">
                    <User className="h-4 w-4 text-gray-400" />
                    <span>Assigned to {assignee.name || assignee.email}</span>
                  </div>
                )}
                {reporter && (
                  <div className="flex items-center gap-2 text-gray-600" data-testid="text-task-reporter">
                    <User className="h-4 w-4 text-emerald-600" />
                    <span>Reported by {reporter.displayName || reporter.name || reporter.email}</span>
                  </div>
                )}
                {task.vendorId && (() => {
                  const vendor = vendors.find(v => v.id === task.vendorId);
                  if (!vendor) return null;
                  const sentAt = task.sentToVendorAt ? new Date(task.sentToVendorAt) : null;
                  return (
                    <div className="flex items-center gap-2 text-gray-600 flex-wrap" data-testid="text-vendor-assigned">
                      <Users className="h-4 w-4 text-gray-400" />
                      <span>{vendor.name}</span>
                      {sentAt ? (
                        <span
                          className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700"
                          data-testid="badge-sent-to-vendor"
                          title={`Sent ${format(sentAt, "MMM d, yyyy 'at' h:mm a")}`}
                        >
                          <CheckCircle2 className="h-3 w-3" />
                          Sent {format(sentAt, "MMM d")}
                        </span>
                      ) : (
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-6 px-2 text-xs"
                          onClick={() => sendToVendorMutation.mutate()}
                          disabled={sendToVendorMutation.isPending}
                          data-testid="button-send-to-vendor"
                        >
                          {sendToVendorMutation.isPending ? (
                            <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                          ) : (
                            <Send className="h-3 w-3 mr-1" />
                          )}
                          {sendToVendorMutation.isPending ? "Sending..." : "Send to vendor"}
                        </Button>
                      )}
                    </div>
                  );
                })()}
                {task.dueDate && (
                  <div className="flex items-center gap-2 text-gray-600">
                    <Calendar className="h-4 w-4 text-gray-400" />
                    <span>{format(new Date(task.dueDate), "MMM d, yyyy")}</span>
                  </div>
                )}
                <div className="flex items-center gap-2 text-gray-500 text-xs">
                  <span>Created {format(new Date(task.createdAt), "MMM d, yyyy")}</span>
                </div>
              </div>

              {/* Task #2187 — Vendor estimate/price breakdown panel */}
              {(task as (typeof task) & { lockedRateType?: string | null; lockedRateCents?: number | null; estimateCents?: number | null; travelFeeCents?: number | null; vendorEstimateNote?: string | null; priceBreakdown?: Record<string, unknown> | null }).lockedRateType &&
               ((task as any).lockedRateCents != null && (task as any).lockedRateCents > 0) && (() => {
                const t = task as (typeof task) & { lockedRateType?: string | null; lockedRateCents?: number | null; estimateHours?: string | null; estimateCents?: number | null; travelFeeCents?: number | null; vendorEstimateNote?: string | null };
                return (
                  <div className="rounded-md border border-blue-100 dark:border-blue-900 bg-blue-50 dark:bg-blue-950/40 p-3 mt-2 space-y-1 text-sm" data-testid="section-vendor-estimate">
                    {task.status === "completed" && task.priceCents !== null && task.priceCents !== undefined ? (
                      // Post-completion: show actual actuals from priceBreakdown
                      (() => {
                        const bd = (task as any).priceBreakdown as Record<string, number | string> | null | undefined;
                        const isHourly = (bd?.rateType ?? t.lockedRateType) === "hourly";
                        const actualHours = bd?.hours as number | undefined;
                        const laborCents = bd?.laborCents as number | undefined;
                        const flatCents = bd?.flatCents as number | undefined;
                        const travelCents = (bd?.travelFeeCents as number | undefined) ?? (t.travelFeeCents ?? 0);
                        return (
                          <>
                            <p className="text-xs font-medium text-blue-800 dark:text-blue-300 uppercase tracking-wide mb-1">Completed — Actual Charges</p>
                            <div className="flex justify-between">
                              <span className="text-gray-600">Rate</span>
                              <span className="font-medium">
                                {isHourly
                                  ? `$${((t.lockedRateCents ?? 0) / 100).toFixed(2)}/hr`
                                  : `$${((t.lockedRateCents ?? 0) / 100).toFixed(2)} flat`}
                              </span>
                            </div>
                            {isHourly && actualHours !== undefined && (
                              <div className="flex justify-between">
                                <span className="text-gray-600">Actual hours</span>
                                <span className="font-medium">{actualHours.toFixed(2)} hrs</span>
                              </div>
                            )}
                            {isHourly && laborCents !== undefined && (
                              <div className="flex justify-between">
                                <span className="text-gray-600">Labor</span>
                                <span className="font-medium">${(laborCents / 100).toFixed(2)}</span>
                              </div>
                            )}
                            {!isHourly && flatCents !== undefined && (
                              <div className="flex justify-between">
                                <span className="text-gray-600">Flat rate</span>
                                <span className="font-medium">${(flatCents / 100).toFixed(2)}</span>
                              </div>
                            )}
                            {travelCents > 0 && (
                              <div className="flex justify-between">
                                <span className="text-gray-600">Travel fee</span>
                                <span className="font-medium">${(travelCents / 100).toFixed(2)}</span>
                              </div>
                            )}
                            <div className="flex justify-between font-semibold border-t border-blue-200 dark:border-blue-800 pt-1 mt-1">
                              <span>Total charged</span>
                              <span>${(task.priceCents / 100).toFixed(2)}</span>
                            </div>
                          </>
                        );
                      })()
                    ) : (
                      // Pre-completion: show estimate
                      <>
                        <p className="text-xs font-medium text-blue-800 dark:text-blue-300 uppercase tracking-wide mb-1">Vendor Estimate</p>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Rate</span>
                          <span className="font-medium">
                            {t.lockedRateType === "hourly"
                              ? `$${((t.lockedRateCents ?? 0) / 100).toFixed(2)}/hr`
                              : `$${((t.lockedRateCents ?? 0) / 100).toFixed(2)} flat`}
                          </span>
                        </div>
                        {t.lockedRateType === "hourly" && t.estimateHours && (
                          <div className="flex justify-between">
                            <span className="text-gray-600">Est. hours</span>
                            <span className="font-medium">{t.estimateHours}</span>
                          </div>
                        )}
                        {t.estimateCents !== null && t.estimateCents !== undefined && (
                          <div className="flex justify-between">
                            <span className="text-gray-600">Labor estimate</span>
                            <span className="font-medium">${(t.estimateCents / 100).toFixed(2)}</span>
                          </div>
                        )}
                        {t.travelFeeCents !== null && t.travelFeeCents !== undefined && t.travelFeeCents > 0 && (
                          <div className="flex justify-between">
                            <span className="text-gray-600">Travel fee</span>
                            <span className="font-medium">${(t.travelFeeCents / 100).toFixed(2)}</span>
                          </div>
                        )}
                      </>
                    )}
                    {t.vendorEstimateNote && (
                      <p className="text-xs text-gray-500 border-t border-blue-200 dark:border-blue-800 pt-1 mt-1 italic">"{t.vendorEstimateNote}"</p>
                    )}
                  </div>
                );
              })()}

              {/* Task #4045 — Owner approval status. When a vendor's agreed cost
                  exceeds the owner's pre-approved threshold the backend auto-raises
                  a pending approval at bid time and blocks scheduling/starting
                  until the owner decides. A pending row renders as a hard banner;
                  approved/rejected show their status (rejection surfaces the note). */}
              {ownerApprovalForTask && (
                ownerApprovalPending ? (
                  <div
                    className="rounded-md border border-brand-accent/40 bg-brand-accent/10 p-3 mt-2"
                    data-testid="section-owner-approval"
                  >
                    <div className="flex items-center gap-2">
                      <AlertCircle className="h-4 w-4 text-brand-accent" />
                      <span className="text-sm font-semibold text-brand-accent" data-testid="badge-owner-approval">
                        Awaiting owner approval
                      </span>
                    </div>
                    <div className="flex justify-between mt-2">
                      <span className="text-xs text-muted-foreground">Vendor estimate</span>
                      <span className="text-xs font-semibold text-brand-accent" data-testid="text-owner-approval-estimate">
                        ${(ownerApprovalForTask.taskCostCents / 100).toFixed(2)}
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1" data-testid="text-owner-approval-blocked">
                      This vendor cost exceeds the owner's pre-approved limit. Scheduling and starting this task are
                      blocked until the owner approves.
                    </p>
                    <Button
                      size="sm"
                      variant="outline"
                      className="mt-2 h-7 text-xs"
                      onClick={() => resendApprovalMutation.mutate()}
                      disabled={resendApprovalMutation.isPending}
                      data-testid="button-resend-owner-approval"
                    >
                      <Bell className="h-3.5 w-3.5 mr-1" />
                      {resendApprovalMutation.isPending ? "Sending…" : "Remind owner"}
                    </Button>
                  </div>
                ) : (
                  <div className="rounded-md border border-gray-200 dark:border-gray-800 p-3 mt-2" data-testid="section-owner-approval">
                    <Badge
                      className={
                        ownerApprovalForTask.status === "approved"
                          ? "bg-green-100 text-green-800"
                          : "bg-red-100 text-red-800"
                      }
                      data-testid="badge-owner-approval"
                    >
                      {ownerApprovalForTask.status === "approved" ? "Owner approved" : "Owner rejected"}
                    </Badge>
                    {ownerApprovalForTask.status === "rejected" && ownerApprovalForTask.ownerNote && (
                      <p className="text-xs text-gray-500 mt-1" data-testid="text-owner-approval-note">
                        Owner note: {ownerApprovalForTask.ownerNote}
                      </p>
                    )}
                  </div>
                )
              )}

              <div className="flex gap-2 pt-1 flex-wrap">
                {task.vendorId && task.status === "completed" && (
                  <Button
                    size="sm"
                    className="bg-purple-600 hover:bg-purple-700 text-white"
                    onClick={() => setProvenPayOpen(true)}
                    disabled={false}
                    data-testid="button-proven-pay"
                  >
                    <DollarSign className="h-3.5 w-3.5 mr-1" />
                    Approve and Pay
                  </Button>
                )}
                {task.vendorId && (task.status === "completed" || task.status === "approved") && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setReviewOpen(true)}
                    data-testid="button-leave-review"
                  >
                    <Star className="h-3.5 w-3.5 mr-1" />
                    Leave a Review
                  </Button>
                )}
                <Button size="sm" variant="outline" onClick={() => { setEditData({ title: task.title, description: task.description, status: task.status, priority: task.priority, category: task.category, propertyId: task.propertyId, assigneeId: task.assigneeId, vendorId: task.vendorId, reporterMemberId: task.reporterMemberId, linkedApplianceId: task.linkedApplianceId, dueDate: task.dueDate, timeTrackingEnabled: task.timeTrackingEnabled }); setEditAttachments(hydrateAttachmentsFromUrls(task.attachmentUrls)); setEditFieldErrors({}); setSuggestedTitle(null); setTitleManuallyEdited(false); setEditing(true); }} data-testid="button-edit-task">Edit</Button>
                {task.vendorId && !["completed", "approved", "paid", "rejected", "cancelled", "complete"].includes(task.status) && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => remindMutation.mutate()}
                    disabled={remindMutation.isPending}
                    data-testid="button-remind-vendor"
                  >
                    <Bell className="h-3.5 w-3.5 mr-1" />
                    {remindMutation.isPending ? "Reminding…" : "Remind vendor"}
                  </Button>
                )}
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => duplicateMutation.mutate()}
                  disabled={duplicateMutation.isPending}
                  data-testid="button-duplicate-task"
                >
                  <Copy className="h-3.5 w-3.5 mr-1" />
                  {duplicateMutation.isPending ? "Duplicating…" : "Duplicate"}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={isDownloading}
                  data-testid="button-download-task-record"
                  onClick={async () => {
                    setIsDownloading(true);
                    try {
                      const res = await fetch(`/api/app/tasks/${task.id}/export`, { credentials: "include" });
                      if (!res.ok) {
                        const data = await res.json().catch(() => ({}));
                        throw new Error((data as any)?.message || "Export failed");
                      }
                      const disposition = res.headers.get("Content-Disposition") ?? "";
                      const fnMatch = disposition.match(/filename="([^"]+)"/);
                      const filename = fnMatch ? fnMatch[1] : `task-${task.id}.pdf`;
                      const blob = await res.blob();
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement("a");
                      a.href = url;
                      a.download = filename;
                      document.body.appendChild(a);
                      a.click();
                      setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(url); }, 1000);
                    } catch (err) {
                      toast({ title: "Download failed", description: parseErrorMessage(err, "Please try again."), variant: "destructive" });
                    } finally {
                      setIsDownloading(false);
                    }
                  }}
                >
                  {isDownloading
                    ? <><Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />Exporting…</>
                    : <><Download className="h-3.5 w-3.5 mr-1" />Download task record</>}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="text-red-600 border-red-200 hover:bg-red-50"
                  onClick={() => { if (confirm("Delete this task?")) deleteMutation.mutate(); }}
                  disabled={deleteMutation.isPending}
                  data-testid="button-delete-task"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-3 border border-gray-200 rounded-lg p-3">
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-xs font-medium text-gray-600">Title</label>
                  {suggestedTitle &&
                    !titleManuallyEdited &&
                    isGenericTaskTitle(editData.title) &&
                    suggestedTitle.trim().toLowerCase() !== (editData.title || "").trim().toLowerCase() && (
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        className="h-6 px-2 text-[11px] gap-1 text-brand border-brand/30 hover:bg-brand-muted"
                        onClick={() => { setEditData({ ...editData, title: suggestedTitle }); clearFieldError("title"); }}
                        data-testid="button-apply-suggested-title"
                        title={`Apply suggested title: ${suggestedTitle}`}
                      >
                        <Wand2 className="h-3 w-3" />
                        Apply suggested title
                      </Button>
                    )}
                </div>
                <Input
                  value={editData.title || ""}
                  onChange={(e) => { setEditData({ ...editData, title: e.target.value }); setTitleManuallyEdited(true); clearFieldError("title"); }}
                  className={editFieldErrors.title ? "border-red-500 focus-visible:ring-red-500" : undefined}
                  aria-invalid={editFieldErrors.title ? true : undefined}
                  data-testid="input-edit-title"
                />
                {suggestedTitle &&
                  !titleManuallyEdited &&
                  isGenericTaskTitle(editData.title) &&
                  suggestedTitle.trim().toLowerCase() !== (editData.title || "").trim().toLowerCase() && (
                    <p className="text-[11px] text-gray-500 mt-1" data-testid="text-suggested-title">
                      Suggested: {suggestedTitle}
                    </p>
                  )}
                {editFieldErrors.title && (
                  <p className="text-xs text-red-600 mt-1" data-testid="error-edit-title">{editFieldErrors.title}</p>
                )}
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-xs font-medium text-gray-600">Description</label>
                  {(linkedChatsQuery.data ?? []).length > 0 ? (
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className="h-6 px-2 text-[11px] gap-1 text-brand border-brand/30 hover:bg-brand-muted"
                      onClick={() => draftDescriptionMutation.mutate()}
                      disabled={draftDescriptionMutation.isPending}
                      data-testid="button-ai-draft-description"
                    >
                      {draftDescriptionMutation.isPending ? (
                        <Loader2 className="h-3 w-3 animate-spin" />
                      ) : (
                        <Wand2 className="h-3 w-3" />
                      )}
                      {draftDescriptionMutation.isPending
                        ? "Drafting…"
                        : draftDescriptionMutation.isSuccess
                          ? "Rerun"
                          : "AI Draft"}
                    </Button>
                  ) : (
                    <span
                      className="text-[11px] text-gray-400 flex items-center gap-1 cursor-default select-none"
                      title="Link messages to this task to enable AI drafting"
                      data-testid="hint-ai-draft-no-messages"
                    >
                      <Wand2 className="h-3 w-3" />
                      AI Draft (link messages first)
                    </span>
                  )}
                </div>
                <Textarea
                  value={editData.description || ""}
                  onChange={(e) => { setEditData({ ...editData, description: e.target.value }); clearFieldError("description"); }}
                  rows={3}
                  className={editFieldErrors.description ? "border-red-500 focus-visible:ring-red-500" : undefined}
                  aria-invalid={editFieldErrors.description ? true : undefined}
                  data-testid="input-edit-description"
                />
                {editFieldErrors.description && (
                  <p className="text-xs text-red-600 mt-1" data-testid="error-edit-description">{editFieldErrors.description}</p>
                )}
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-xs font-medium text-gray-600 mb-1 block">Status</label>
                  <Select
                    value={editData.status || task.status}
                    onValueChange={(v) => {
                      // Task #4284 — "Paid / Closed" never sets the status directly;
                      // it always opens the Approve Payment popup (charge OR manual
                      // amount, $0 allowed). The task lands in Paid / Closed only
                      // once a payment/archive record is created there.
                      if (v === "paid") {
                        setProvenPayOpen(true);
                        return;
                      }
                      setEditData({ ...editData, status: v as MaintenanceTask["status"] });
                      clearFieldError("status");
                    }}
                  >
                    <SelectTrigger className={`h-8 text-xs${editFieldErrors.status ? " border-red-500 focus:ring-red-500" : ""}`} data-testid="select-edit-status">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="requests">Pending</SelectItem>
                      <SelectItem value="open">Open</SelectItem>
                      <SelectItem value="in_progress">In Progress</SelectItem>
                      <SelectItem value="on_hold">On Hold</SelectItem>
                      <SelectItem value="complete">Complete</SelectItem>
                      <SelectItem value="completed">Payment Pending</SelectItem>
                      <SelectItem value="paid">Paid / Closed</SelectItem>
                    </SelectContent>
                  </Select>
                  {editFieldErrors.status && (
                    <p className="text-xs text-red-600 mt-1" data-testid="error-edit-status">{editFieldErrors.status}</p>
                  )}
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-600 mb-1 block">Priority</label>
                  <Select value={editData.priority || task.priority} onValueChange={(v) => { setEditData({ ...editData, priority: v as MaintenanceTask["priority"] }); clearFieldError("priority"); }}>
                    <SelectTrigger className={`h-8 text-xs${editFieldErrors.priority ? " border-red-500 focus:ring-red-500" : ""}`} data-testid="select-edit-priority">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="urgent">{priorityWithSla(slaConfig, "urgent")}</SelectItem>
                      <SelectItem value="high">{priorityWithSla(slaConfig, "high")}</SelectItem>
                      <SelectItem value="medium">{priorityWithSla(slaConfig, "medium")}</SelectItem>
                      <SelectItem value="low">{priorityWithSla(slaConfig, "low")}</SelectItem>
                      <SelectItem value="batch">Batch (hold for grouping)</SelectItem>
                    </SelectContent>
                  </Select>
                  {editFieldErrors.priority && (
                    <p className="text-xs text-red-600 mt-1" data-testid="error-edit-priority">{editFieldErrors.priority}</p>
                  )}
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-600 mb-1 block">Category</label>
                  <Select value={editData.category || "none"} onValueChange={(v) => { const nextCategory = v === "none" ? null : v; setEditData((prev) => { const next = { ...prev, category: nextCategory }; if (nextCategory === "Room Turn" && prev.vendorId != null) { const sel = vendors.find((vd) => vd.id === prev.vendorId); const roles = sel && Array.isArray(sel.roles) ? (sel.roles as string[]) : []; if (!roles.includes("cleaning")) next.vendorId = null; } return next; }); clearFieldError("category"); clearFieldError("vendorId"); }}>
                    <SelectTrigger className={`h-8 text-xs${editFieldErrors.category ? " border-red-500 focus:ring-red-500" : ""}`} data-testid="select-edit-category">
                      <SelectValue placeholder="None" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No category</SelectItem>
                      {MAINTENANCE_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  {editFieldErrors.category && (
                    <p className="text-xs text-red-600 mt-1" data-testid="error-edit-category">{editFieldErrors.category}</p>
                  )}
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-600 mb-1 block">Property</label>
                  <Select value={editData.propertyId ? String(editData.propertyId) : "none"} onValueChange={(v) => { setEditData({ ...editData, propertyId: v === "none" ? null : parseInt(v, 10), reporterMemberId: null, linkedApplianceId: null }); clearFieldError("propertyId"); clearFieldError("reporterMemberId"); clearFieldError("linkedApplianceId"); }}>
                    <SelectTrigger className={`h-8 text-xs${editFieldErrors.propertyId ? " border-red-500 focus:ring-red-500" : ""}`} data-testid="select-edit-property">
                      <SelectValue placeholder="None" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No property</SelectItem>
                      {sortProperties(properties).map((p) => <SelectItem key={p.id} value={String(p.id)}>{p.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  {editFieldErrors.propertyId && (
                    <p className="text-xs text-red-600 mt-1" data-testid="error-edit-property">{editFieldErrors.propertyId}</p>
                  )}
                </div>
                {editData.category === "Appliances" && (
                  <LinkedApplianceEditField
                    editData={editData}
                    setEditData={setEditData}
                    editFieldErrors={editFieldErrors}
                    clearFieldError={clearFieldError}
                  />
                )}
                <div>
                  <label className="text-xs font-medium text-gray-600 mb-1 block">Assignee</label>
                  <Select value={editData.assigneeId != null ? String(editData.assigneeId) : "none"} onValueChange={(v) => { setEditData({ ...editData, assigneeId: v === "none" ? null : parseInt(v, 10) }); clearFieldError("assigneeId"); }}>
                    <SelectTrigger className={`h-8 text-xs${editFieldErrors.assigneeId ? " border-red-500 focus:ring-red-500" : ""}`} data-testid="select-edit-assignee">
                      <SelectValue placeholder="Unassigned" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Unassigned</SelectItem>
                      <SelectItem value="0">Owner (You)</SelectItem>
                      {teamMembers.filter((m) => m.status !== "declined").map((m) => <SelectItem key={m.id} value={String(m.id)}>{m.name || m.email}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  {editFieldErrors.assigneeId && (
                    <p className="text-xs text-red-600 mt-1" data-testid="error-edit-assignee">{editFieldErrors.assigneeId}</p>
                  )}
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-600 mb-1 block">Vendor</label>
                  <Select value={editData.vendorId != null ? String(editData.vendorId) : "none"} onValueChange={(v) => { setEditData({ ...editData, vendorId: v === "none" ? null : parseInt(v, 10) }); clearFieldError("vendorId"); }}>
                    <SelectTrigger className={`h-8 text-xs${editFieldErrors.vendorId ? " border-red-500 focus:ring-red-500" : ""}`} data-testid="select-edit-vendor">
                      <SelectValue placeholder="No vendor" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No vendor</SelectItem>
                      {propertyVendors.map((v) => (
                        <SelectItem key={v.id} value={String(v.id)} data-testid={`option-vendor-${v.id}`}>
                          <span className="flex items-center gap-2">
                            {v.priority != null ? (
                              <span
                                className="inline-flex items-center justify-center rounded bg-gray-100 text-gray-700 text-[10px] font-medium px-1.5 py-0.5 min-w-[1.5rem]"
                                data-testid={`badge-vendor-priority-${v.id}`}
                              >
                                #{v.priority}
                              </span>
                            ) : (
                              <span
                                className="inline-flex items-center justify-center rounded bg-gray-50 text-gray-400 text-[10px] font-medium px-1.5 py-0.5"
                                data-testid={`badge-vendor-unranked-${v.id}`}
                              >
                                unranked
                              </span>
                            )}
                            <span>{v.name}</span>
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {editFieldErrors.vendorId && (
                    <p className="text-xs text-red-600 mt-1" data-testid="error-edit-vendor">{editFieldErrors.vendorId}</p>
                  )}
                </div>
                <div className="col-span-2">
                  <label className="text-xs font-medium text-gray-600 mb-1 block">Reported by (member)</label>
                  <Select
                    value={editData.reporterMemberId != null ? String(editData.reporterMemberId) : "none"}
                    onValueChange={(v) => { setEditData({ ...editData, reporterMemberId: v === "none" ? null : parseInt(v, 10) }); clearFieldError("reporterMemberId"); }}
                    disabled={!activePropertyId}
                  >
                    <SelectTrigger className={`h-8 text-xs${editFieldErrors.reporterMemberId ? " border-red-500 focus:ring-red-500" : ""}`} data-testid="select-edit-reporter">
                      <SelectValue placeholder={activePropertyId ? "No member" : "Select a property first"} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No member</SelectItem>
                      {reporterMembers.map((m) => (
                        <SelectItem key={m.id} value={String(m.id)} data-testid={`option-edit-reporter-${m.id}`}>
                          {m.displayName || m.name || m.email}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {editFieldErrors.reporterMemberId && (
                    <p className="text-xs text-red-600 mt-1" data-testid="error-edit-reporter">{editFieldErrors.reporterMemberId}</p>
                  )}
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">Due Date</label>
                <Input
                  type="date"
                  value={editData.dueDate ? new Date(editData.dueDate).toISOString().split("T")[0] : ""}
                  onChange={(e) => { setEditData({ ...editData, dueDate: e.target.value ? new Date(e.target.value) : null }); clearFieldError("dueDate"); }}
                  className={`h-8 text-xs${editFieldErrors.dueDate ? " border-red-500 focus-visible:ring-red-500" : ""}`}
                  aria-invalid={editFieldErrors.dueDate ? true : undefined}
                  data-testid="input-edit-duedate"
                />
                {editFieldErrors.dueDate && (
                  <p className="text-xs text-red-600 mt-1" data-testid="error-edit-duedate">{editFieldErrors.dueDate}</p>
                )}
              </div>
              <div>
                {/* Forms-on-tasks — per-task timer visibility. Off = the vendor
                    portal hides the timer and the server rejects timer writes. */}
                <label className="flex items-center gap-2 text-xs font-medium text-gray-600 cursor-pointer">
                  <Checkbox
                    checked={editData.timeTrackingEnabled !== false}
                    onCheckedChange={(v) => setEditData({ ...editData, timeTrackingEnabled: v === true })}
                    data-testid="checkbox-edit-time-tracking"
                  />
                  Vendor time tracking enabled
                </label>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">Attachments</label>
                <AttachmentComposer
                  attachments={editAttachments}
                  setAttachments={setEditAttachments}
                  disabled={updateMutation.isPending}
                  testIdPrefix="edit-attach"
                />
              </div>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  onClick={() => {
                    if (editAttachments.some((a) => a.status === "uploading")) {
                      toast({ title: "Wait for uploads to finish" });
                      return;
                    }
                    updateMutation.mutate({
                      ...editData,
                      attachmentUrls: buildReadyAttachmentUrls(editAttachments),
                    } as any);
                  }}
                  disabled={updateMutation.isPending}
                  data-testid="button-save-task"
                >
                  {updateMutation.isPending ? "Saving..." : "Save"}
                </Button>
                <Button size="sm" variant="outline" onClick={() => { setEditing(false); setEditFieldErrors({}); setEditAttachments([]); setSuggestedTitle(null); }}>Cancel</Button>
              </div>
            </div>
          )}

          {/* Task #2024 — host-facing checklist. Hosts can toggle each item;
              completed items show "Checked <date> by <name>" using the
              `completedByName` returned by the GET endpoint. */}
          {(checklistQuery.data ?? []).length > 0 && (
            <div className="border-t border-gray-200 pt-4" data-testid="section-checklist">
              <h3 className="font-medium text-sm text-gray-900 mb-3 flex items-center gap-1.5">
                <CheckCircle2 className="h-4 w-4 text-blue-600" />
                Checklist
              </h3>
              <div className="space-y-2">
                {(checklistQuery.data ?? []).map((item) => {
                  const checked = item.completedAt != null;
                  return (
                    <div key={item.id} className="flex items-start gap-2" data-testid={`checklist-item-${item.id}`}>
                      <Checkbox
                        id={`checklist-${item.id}`}
                        checked={checked}
                        disabled={toggleChecklistMutation.isPending}
                        onCheckedChange={(v) =>
                          toggleChecklistMutation.mutate({ itemId: item.id, checked: v === true })
                        }
                        className="mt-0.5"
                        data-testid={`checkbox-checklist-${item.id}`}
                      />
                      <label
                        htmlFor={`checklist-${item.id}`}
                        className="flex-1 cursor-pointer select-none text-sm leading-snug"
                      >
                        <span className={checked ? "text-gray-500 line-through" : "text-gray-900"} data-testid={`text-checklist-${item.id}`}>
                          {item.text}
                        </span>
                        {checked && item.completedAt && (
                          <span className="block text-[11px] text-gray-500 mt-0.5" data-testid={`meta-checklist-${item.id}`}>
                            Checked {format(new Date(item.completedAt), "MMM d, yyyy")}
                            {item.completedByName ? ` by ${item.completedByName}` : ""}
                          </span>
                        )}
                        {item.photoRequired && !item.photoUrl && !checked && (
                          <span className="block text-[11px] text-gray-500 mt-0.5">Photo required</span>
                        )}
                      </label>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Forms-on-tasks — the form-task's template + submissions (location intact). */}
          {task.formTemplateId != null && (
            <div className="border-t border-gray-200 pt-4" data-testid="section-task-form">
              <h3 className="font-medium text-sm text-gray-900 mb-3 flex items-center gap-1.5">
                <ClipboardCheck className="h-4 w-4 text-brand" />
                Service form{taskFormQuery.data?.template ? `: ${taskFormQuery.data.template.name}` : ""}
              </h3>
              {taskFormQuery.isLoading ? (
                <p className="text-xs text-gray-400">Loading form…</p>
              ) : taskFormQuery.isError ? (
                <p className="text-xs text-red-500">Failed to load the form.</p>
              ) : (taskFormQuery.data?.submissions.length ?? 0) === 0 ? (
                <p className="text-xs text-gray-500" data-testid="text-form-awaiting-submission">
                  Waiting on the vendor's submission — it will appear here with photos and location.
                </p>
              ) : (
                <div className="space-y-3">
                  {taskFormQuery.data!.submissions.map((sub) => {
                    const template = taskFormQuery.data!.template;
                    const responses = (sub.checklistResponses || {}) as Record<string, boolean>;
                    const photoMap = (sub.photoUrls || {}) as Record<string, string[]>;
                    const sections = (template.sections || []) as Array<{
                      id: string;
                      title: string;
                      checklistItems: Array<{ id: string; label: string }>;
                      photoSlots: Array<{ id: string; label: string }>;
                    }>;
                    return (
                      <div key={sub.id} className="border border-gray-200 rounded-lg p-3 space-y-2" data-testid={`form-submission-${sub.id}`}>
                        <div className="flex items-center justify-between flex-wrap gap-1">
                          <p className="text-xs text-gray-600">
                            Submitted {format(new Date(sub.submittedAt), "MMM d, yyyy h:mm a")}
                            {sub.submitterName ? ` by ${sub.submitterName}` : ""}
                            {sub.dateOfService ? ` · Service date: ${sub.dateOfService}` : ""}
                          </p>
                          {sub.locationFlagged ? (
                            <Badge className="bg-amber-100 text-amber-800" data-testid={`badge-location-flagged-${sub.id}`}>
                              <MapPin className="h-3 w-3 mr-1" />
                              {sub.locationDistanceMiles != null ? `${sub.locationDistanceMiles.toFixed(1)} mi away` : "Location flagged"}
                            </Badge>
                          ) : sub.latitude != null ? (
                            <Badge className="bg-green-100 text-green-800" data-testid={`badge-location-ok-${sub.id}`}>
                              <MapPin className="h-3 w-3 mr-1" />
                              On-site
                            </Badge>
                          ) : null}
                        </div>
                        {sub.latitude != null && sub.longitude != null && (
                          <a
                            href={`https://www.google.com/maps?q=${sub.latitude},${sub.longitude}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-brand hover:underline inline-flex items-center gap-1"
                            data-testid={`link-submission-location-${sub.id}`}
                          >
                            <ExternalLink className="h-3 w-3" />
                            View location on map
                          </a>
                        )}
                        {sections.map((section) => {
                          const items = section.checklistItems || [];
                          const slotPhotos = (section.photoSlots || []).flatMap((slot) => (photoMap[slot.id] || []).map((url) => ({ url, label: slot.label })));
                          if (items.length === 0 && slotPhotos.length === 0) return null;
                          return (
                            <div key={section.id} className="text-xs">
                              <p className="font-medium text-gray-700 mb-1">{section.title}</p>
                              {items.length > 0 && (
                                <ul className="space-y-0.5 mb-1">
                                  {items.map((item) => (
                                    <li key={item.id} className="flex items-center gap-1.5">
                                      {responses[item.id] ? (
                                        <CheckCircle2 className="h-3 w-3 text-green-600 shrink-0" />
                                      ) : (
                                        <X className="h-3 w-3 text-red-400 shrink-0" />
                                      )}
                                      <span className={responses[item.id] ? "text-gray-600" : "text-gray-400"}>{item.label}</span>
                                    </li>
                                  ))}
                                </ul>
                              )}
                              {slotPhotos.length > 0 && (
                                <div className="flex flex-wrap gap-2">
                                  {slotPhotos.map((p, i) => (
                                    <a key={i} href={p.url} target="_blank" rel="noopener noreferrer" title={p.label}>
                                      <img src={p.url} alt={p.label} className="w-16 h-16 object-cover rounded border" />
                                    </a>
                                  ))}
                                </div>
                              )}
                            </div>
                          );
                        })}
                        {sub.notes && (
                          <p className="text-xs text-gray-600 whitespace-pre-wrap border-t border-gray-100 pt-2">{sub.notes}</p>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {((linkedChatsQuery.data ?? []).length > 0 || linkedChatsQuery.isLoading) && (
            <div className="border-t border-gray-200 pt-4" data-testid="section-linked-messages">
              <h3 className="font-medium text-sm text-gray-900 mb-3 flex items-center gap-1.5">
                <MessageCircle className="h-4 w-4 text-green-600" />
                Linked messages
              </h3>
              {linkedChatsQuery.isLoading ? (
                <div className="text-xs text-gray-400 py-2">Loading…</div>
              ) : (
                <div className="space-y-2 mb-2">
                  {(linkedChatsQuery.data ?? []).map((lc) => (
                    <div
                      key={lc.chatId}
                      className="rounded-lg border border-green-100 dark:border-green-900 bg-green-50 dark:bg-green-950/40 px-3 py-2"
                      data-testid={`linked-chat-${lc.chatId}`}
                    >
                      <div className="flex items-center justify-between gap-2 mb-1">
                        <span className="text-xs font-medium text-gray-800">{lc.senderName}</span>
                        <div className="flex items-center gap-2">
                          <span className="text-[11px] text-gray-400">
                            {format(new Date(lc.createdAt), "MMM d, h:mm a")}
                          </span>
                          <Link
                            href={`/app/messages?member=${lc.memberId}&chat=${lc.chatId}`}
                            className="inline-flex items-center gap-0.5 text-[11px] text-green-700 hover:text-green-900"
                            title="Open in messages"
                            data-testid={`link-open-chat-${lc.chatId}`}
                          >
                            <ExternalLink className="h-3 w-3" />
                            View
                          </Link>
                          <button
                            type="button"
                            onClick={() => confirmUnlinkChat(lc.chatId)}
                            disabled={unlinkingChatIds.has(lc.chatId)}
                            className="inline-flex items-center gap-0.5 text-[11px] text-gray-500 hover:text-red-600 disabled:opacity-50"
                            title="Remove link"
                            data-testid={`button-unlink-chat-${lc.chatId}`}
                          >
                            <Unlink className="h-3 w-3" />
                            Remove link
                          </button>
                        </div>
                      </div>
                      {lc.messageText && (
                        <p className="text-sm text-gray-700 whitespace-pre-wrap">{lc.messageText}</p>
                      )}
                      {lc.attachmentUrls && lc.attachmentUrls.length > 0 && (
                        <div className="mt-1">
                          <MessageAttachments urls={lc.attachmentUrls} messageId={`linked-chat-${lc.chatId}`} />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Task #1654 — Two-way chat with the assigned vendor (replaces SMS). */}
          {task.vendorId && task.sentToVendorAt && (
            <VendorChatPanel taskId={task.id} vendorName={vendors.find(v => v.id === task.vendorId)?.name || "Vendor"} />
          )}

          {/* Status history timeline */}
          {((statusHistoryQuery.data ?? []).length > 0 || statusHistoryQuery.isLoading) && (
            <div className="border-t border-gray-200 pt-4" data-testid="section-status-history">
              <h3 className="font-medium text-sm text-gray-900 mb-3 flex items-center gap-1.5">
                <Clock className="h-4 w-4 text-brand" />
                Status history
              </h3>
              {statusHistoryQuery.isLoading ? (
                <div className="text-xs text-gray-400 py-2">Loading…</div>
              ) : (
                <ol className="relative border-l border-gray-200 ml-2 space-y-3" data-testid="list-status-history">
                  {(statusHistoryQuery.data ?? []).map((entry, i) => {
                    const fromLabel = entry.fromStatus ? entry.fromStatus.replace(/_/g, " ") : "new task";
                    const toLabel = entry.toStatus.replace(/_/g, " ");
                    const actorTypeLabel = entry.actorType === "host" ? "Host" : entry.actorType === "vendor" ? "Vendor" : "System";
                    const actor = entry.changedByName ? `${entry.changedByName} (${actorTypeLabel})` : actorTypeLabel;
                    const actorBadge = entry.actorType === "host"
                      ? <span className="inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-medium bg-brand-muted text-brand" data-testid={`badge-actor-type-${i}`}>Host</span>
                      : entry.actorType === "vendor"
                      ? <span className="inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-medium bg-orange-50 text-orange-700" data-testid={`badge-actor-type-${i}`}>Vendor</span>
                      : <span className="inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-medium bg-gray-100 text-gray-500" data-testid={`badge-actor-type-${i}`}>System</span>;
                    return (
                      <li key={i} className="ml-4" data-testid={`status-history-entry-${i}`}>
                        <span className="absolute -left-1.5 mt-1 h-3 w-3 rounded-full border border-white bg-gray-300" />
                        <p className="text-xs font-medium text-gray-800 capitalize">
                          {fromLabel} → {toLabel}
                        </p>
                        <p className="text-[11px] text-gray-500 flex items-center gap-1 flex-wrap">
                          {format(new Date(entry.changedAt), "MMM d, yyyy 'at' h:mm a")} · {actor} {actorBadge}
                        </p>
                        {entry.note && (
                          <p className="text-[11px] text-gray-400 italic mt-0.5">{entry.note}</p>
                        )}
                      </li>
                    );
                  })}
                </ol>
              )}
            </div>
          )}

          <div className="border-t border-gray-200 pt-4">
            <h3 className="font-medium text-sm text-gray-900 mb-3">Comments</h3>
            <div className="mb-3 max-h-64 overflow-y-auto">
              {comments.length === 0 ? (
                <p className="text-sm text-gray-400 text-center py-4">No comments yet</p>
              ) : (
                comments.map((c) => (
                  <CommentItem key={c.id} comment={c} teamMembers={teamMembers} currentUser={currentUser} isFocused={focusCommentId != null && c.id === focusCommentId} />
                ))
              )}
            </div>
            <div className="space-y-2">
              <MentionTextarea
                value={commentText}
                onChange={(v) => { setCommentText(v); if (commentError) setCommentError(null); }}
                teamMembers={teamMembers}
                placeholder="Add a comment... type @ to mention someone"
              />
              {commentError && (
                <p className="text-xs text-red-600" data-testid="error-comment">{commentError}</p>
              )}
              <AttachmentComposer
                attachments={commentAttachments}
                setAttachments={setCommentAttachments}
                disabled={commentMutation.isPending}
                testIdPrefix="comment-attach"
              />
              <div className="flex justify-end">
                <Button
                  size="sm"
                  onClick={handlePostComment}
                  disabled={
                    commentMutation.isPending ||
                    (!commentText.trim() && commentAttachments.filter((a) => a.status === "ready").length === 0)
                  }
                  data-testid="button-post-comment"
                >
                  <Send className="h-3.5 w-3.5 mr-1.5" />
                  {commentMutation.isPending ? "Posting..." : "Post"}
                </Button>
              </div>
            </div>
          </div>
        </div>
        </>)}
      </SheetContent>
    </Sheet>
    <AlertDialog open={chatToUnlink !== null} onOpenChange={(v) => !v && setChatToUnlink(null)}>
      <AlertDialogContent data-testid="dialog-unlink-chat-confirm">
        <AlertDialogHeader>
          <AlertDialogTitle>Remove the link to this chat?</AlertDialogTitle>
          <AlertDialogDescription>
            The message will no longer appear under this ticket's linked messages. The original "added info from …" comment on the ticket will be rewritten to note that the link was removed; its attachments stay put.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="button-cancel-unlink-chat">Cancel</AlertDialogCancel>
          <AlertDialogAction
            data-testid="button-confirm-unlink-chat"
            onClick={() => {
              if (chatToUnlink === null) return;
              const id = chatToUnlink;
              setUnlinkingChatIds((prev) => new Set([...prev, id]));
              unlinkChatMutation.mutate(id);
            }}
            className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
          >
            Unlink
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    <ApproveAndPayModal
      task={provenPayOpen && task ? (task as any) : null}
      open={provenPayOpen}
      onClose={() => setProvenPayOpen(false)}
    />
    <LeaveReviewModal
      task={reviewOpen && task ? (task as any) : null}
      open={reviewOpen}
      onClose={() => setReviewOpen(false)}
    />
    </>
  );
}
